import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { UnlockIcon } from '../../common/components';
import { managementService } from '../service/management.service';
import { IProductLicense, productLicenseToRow } from '../types.type';
import { shortString, transformDateString } from '../util';
import { ManagementTable } from './table';
import { OfflineActivation } from './offline-activation.component';

const EYE_SLUG = 'squarebit-eye';
const QUERY_KEY = ['eye-license'];

// A checkout redirect lands before the Stripe webhook does, so the licence
// appears a few seconds later. Poll fast but for a bounded window — after this
// the licence genuinely is not coming and something needs a human.
const PROVISIONING_POLL_MS = 2000;
const PROVISIONING_WINDOW_MS = 60000;
// Enough to notice a machine activating elsewhere while you alt-tab to Maya,
// cheap enough to run from every open tab: two indexed selects.
const IDLE_POLL_MS = 5000;

const StatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const styles: Record<string, string> = {
    active: 'bg-badge-green-bg/60 border-badge-green-text/30 text-badge-green-text',
    suspended: 'bg-badge-red-bg/60 border-badge-red-text/30 text-badge-red-text',
    revoked: 'bg-badge-red-bg/60 border-badge-red-text/30 text-badge-red-text',
    lapsed: 'bg-surface-secondary border-border text-content-muted'
  };
  return (
    <span
      className={`inline-block px-[9px] py-[3px] rounded-[5px] border font-mono text-[10px] tracking-[0.06em] uppercase w-fit ${
        styles[status] ?? 'bg-surface-secondary border-border text-content-muted'
      }`}
    >
      {t(`management.eye.license.status.${status}`, { defaultValue: status })}
    </span>
  );
};

/**
 * The licence block inside the Squarebit Eye card: key, seat usage, and the
 * machines currently holding those seats with a per-row release.
 *
 * A NEW SIBLING rather than an edit to SquarebitEyeCard's existing download and
 * EULA logic, and it renders `null` unless the account actually holds an Eye
 * licence — so with the backend flag off (the endpoint 404s, the service maps
 * that to an empty list) the page is pixel-identical to before.
 */
export const EyeLicenseSection = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isCheckoutReturn = searchParams.get('checkout') === 'success';
  const mountedAt = useRef(Date.now());

  // Polling costs nothing in a background tab if we simply stop polling there.
  const [isVisible, setIsVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  useEffect(() => {
    const onVisibilityChange = () =>
      setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const licensesQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => managementService.getMyLicenses(),
    // Per-query overrides ONLY. The global client sets staleTime to 24h with
    // refetchOnMount off, which is right for the rest of the site and wrong
    // here; changing it globally would multiply request volume site-wide.
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: (query) => {
      if (!isVisible) return false;
      const licenses = (query.state.data as IProductLicense[] | undefined) ?? [];
      const hasEyeLicense = licenses.some((l) => l.productSlug === EYE_SLUG);
      if (
        isCheckoutReturn &&
        !hasEyeLicense &&
        Date.now() - mountedAt.current < PROVISIONING_WINDOW_MS
      ) {
        return PROVISIONING_POLL_MS;
      }
      return hasEyeLicense ? IDLE_POLL_MS : false;
    }
  });

  const release = useMutation({
    mutationFn: (activationId: number) =>
      managementService.releaseActivation(activationId),
    // Own action, so reflect it immediately rather than waiting a round trip.
    onMutate: async (activationId: number) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<IProductLicense[]>(QUERY_KEY);
      queryClient.setQueryData<IProductLicense[]>(QUERY_KEY, (old) =>
        old?.map((license) => {
          const held = license.activations.some((a) => a.id === activationId);
          if (!held) return license;
          return {
            ...license,
            usedSeats: Math.max(0, license.usedSeats - 1),
            activations: license.activations.filter((a) => a.id !== activationId)
          };
        })
      );
      return { previous };
    },
    onError: (_error, _activationId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
      }
      toast.error(t('management.eye.license.releaseError'));
    },
    onSuccess: () => toast.success(t('management.eye.license.releaseSuccess')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  });

  const licenses = (licensesQuery.data ?? []).filter(
    (l) => l.productSlug === EYE_SLUG
  );

  // Nothing to show yet, but a checkout just completed: say so rather than
  // leaving a gap where the licence is about to appear.
  if (!licenses.length) {
    if (
      isCheckoutReturn &&
      Date.now() - mountedAt.current < PROVISIONING_WINDOW_MS
    ) {
      return (
        <div className="mt-[22px] pt-[20px] border-t border-border-dark/[0.07]">
          <div className="flex items-center gap-[10px]">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            <span className="font-body text-[13px] text-content-muted-alt">
              {t('management.eye.license.provisioning')}
            </span>
          </div>
        </div>
      );
    }
    return null;
  }

  // The key, its billing type, its expiry and its seat count are EXACTLY the
  // questions the LICENSE KEYS table already answers for every other product
  // on this page, so they are answered here in the same words and the same
  // pills - a customer holding Eye and TouchPose used to meet two unrelated
  // presentations of one idea. What stays below the table is what no other
  // product has: the machines holding the seats, and air-gapped activation.
  const rows = licenses.map(productLicenseToRow);

  return (
    <div className="mt-[22px] pt-[20px] border-t border-border-dark/[0.07]">
      <ManagementTable data={rows} />

      {licenses.map((license) => (
        <div key={license.id} className="mt-[24px]">
          <div className="flex items-center gap-[8px] mb-[10px] flex-wrap">
            <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light">
              {t('management.eye.license.machines')}
            </span>
            {/* WHICH key these machines are on. An account can legitimately
                hold three - one claimed free, one an admin comped, one bought
                - and with one machines list per licence there would otherwise
                be nothing tying a list to the row above it. Shown only when
                there is more than one, so the common case stays quiet. */}
            {licenses.length > 1 && (
              <span className="font-mono text-[10px] tracking-[0.05em] text-content-muted-alt">
                {shortString(license.key)}
              </span>
            )}
            {/* A licence that is not active is the reason its seats will not
                take, so it is said here rather than left to be inferred from
                a plugin that refuses to activate. */}
            {license.status !== 'active' && <StatusBadge status={license.status} />}
          </div>

          {license.status !== 'active' && license.statusReason && (
            <p className="mb-[10px] font-body text-[12.5px] text-content-muted-alt leading-[1.5]">
              {license.statusReason}
            </p>
          )}

          {license.activations.length === 0 ? (
            <p className="font-body text-[13px] text-content-muted-alt leading-[1.5]">
              {t('management.eye.license.noMachines')}
            </p>
          ) : (
            <div className="flex flex-col gap-[8px]">
              {license.activations.map((activation) => (
                <div
                  key={activation.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-[8px] sm:gap-[12px] px-[14px] py-[11px] bg-surface-section border border-border-dark/[0.11] rounded-[10px]"
                >
                  <div className="min-w-0 grow">
                    <div className="font-body text-[13.5px] font-medium text-content-dark truncate">
                      {activation.machineLabel ||
                        t('management.eye.license.unnamedMachine')}
                    </div>
                    <div className="font-mono text-[11px] text-content-muted-alt truncate">
                      {[activation.os, activation.hostApp, activation.appVersion]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </div>
                    {activation.activationMode === 'offline' && (
                      <div className="mt-[3px] flex items-center gap-[6px] flex-wrap">
                        <span className="inline-block px-[6px] py-px rounded-[4px] bg-surface-hover border border-blue-500 font-mono text-[9.5px] tracking-[0.06em] uppercase text-blue">
                          {t('management.eye.license.offline.badge')}
                        </span>
                        {activation.tokenExpiresAt && (
                          <span className="font-body text-[11px] text-content-muted-alt">
                            {t('management.eye.license.offline.expires', {
                              date: transformDateString(activation.tokenExpiresAt)
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="font-body text-[12px] text-content-muted-alt shrink-0">
                    {t('management.eye.license.lastSeen', {
                      date: transformDateString(activation.lastSeenAt)
                    })}
                  </span>
                  <button
                    type="button"
                    title={t('management.eye.license.releaseTitle')}
                    disabled={release.isPending}
                    onClick={() => {
                      if (
                        window.confirm(t('management.eye.license.releaseConfirm'))
                      ) {
                        release.mutate(activation.id);
                      }
                    }}
                    className="inline-flex items-center gap-[4px] px-[8px] h-[26px] rounded-full bg-badge-red-bg/60 border border-badge-red-text/30 text-badge-red-text font-mono text-[10px] font-medium transition-all hover:bg-badge-red-bg hover:border-badge-red-text/50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shrink-0 self-start sm:self-auto"
                  >
                    <UnlockIcon />
                    {t('management.eye.license.release')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <p className="mt-[14px] font-body italic text-[12px] text-content-muted-alt leading-[1.5]">
        {t('management.eye.license.note')}
      </p>

      {/* Air-gapped activation. Collapsed by default - most customers are
          online and should not have to read past a workflow that does not
          apply to them. Once per account, not once per licence: the workflow
          asks which licence to activate against. */}
      <OfflineActivation queryKey={QUERY_KEY} />
    </div>
  );
};
